import { useState, useEffect, useCallback } from 'react';
import { X, Navigation } from 'lucide-react';

const CARDINAL = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO'];

const getCardinal = (deg) => CARDINAL[Math.round(deg / 22.5) % 16];

const CompassWidget = ({ isOpen, onClose }) => {
  const [heading, setHeading] = useState(null);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState('pending');

  const handleOrientation = useCallback((e) => {
    if (e.alpha !== null) {
      // On iOS webkitCompassHeading gives magnetic north directly
      const h = e.webkitCompassHeading || (360 - e.alpha);
      setHeading(Math.round(h) % 360);
      setError(null);
    }
  }, []);

  const requestPermission = async () => {
    try {
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        const perm = await DeviceOrientationEvent.requestPermission();
        if (perm === 'granted') {
          setPermission('granted');
          window.addEventListener('deviceorientation', handleOrientation, true);
        } else {
          setError("Accès au capteur refusé");
          setPermission('denied');
        }
      } else {
        setPermission('granted');
        window.addEventListener('deviceorientation', handleOrientation, true);
      }
    } catch {
      setError("Capteur non disponible sur cet appareil");
      setPermission('denied');
    }
  };

  useEffect(() => {
    if (isOpen && permission === 'pending') {
      requestPermission();
    }
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const deg = heading || 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="compass-widget" onClick={onClose}>
      <div className="bg-[#0a1628] rounded-2xl shadow-2xl w-full max-w-sm p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-white" data-testid="compass-close">
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-center text-white font-bold text-lg mb-4">Compas</h2>

        {/* Compas visuel */}
        <div className="relative w-64 h-64 mx-auto">
          {/* Rose des vents - tourne */}
          <svg viewBox="0 0 200 200" className="w-full h-full transition-transform duration-200 ease-out"
            style={{ transform: `rotate(${-deg}deg)` }}>
            {/* Cercle extérieur */}
            <circle cx="100" cy="100" r="95" fill="none" stroke="#1e3a5f" strokeWidth="2" />
            <circle cx="100" cy="100" r="85" fill="none" stroke="#1e3a5f" strokeWidth="0.5" />

            {/* Graduations */}
            {Array.from({ length: 72 }, (_, i) => {
              const angle = i * 5;
              const isMajor = angle % 30 === 0;
              const r1 = isMajor ? 78 : 82;
              const r2 = 90;
              const rad = (angle * Math.PI) / 180;
              return (
                <line key={i}
                  x1={100 + r1 * Math.sin(rad)} y1={100 - r1 * Math.cos(rad)}
                  x2={100 + r2 * Math.sin(rad)} y2={100 - r2 * Math.cos(rad)}
                  stroke={isMajor ? '#94a3b8' : '#334155'} strokeWidth={isMajor ? 1.5 : 0.5} />
              );
            })}

            {/* Labels cardinaux */}
            {[
              { label: 'N', angle: 0, color: '#ef4444' },
              { label: 'E', angle: 90, color: '#94a3b8' },
              { label: 'S', angle: 180, color: '#94a3b8' },
              { label: 'O', angle: 270, color: '#94a3b8' },
            ].map(({ label, angle, color }) => {
              const rad = (angle * Math.PI) / 180;
              return (
                <text key={label} x={100 + 68 * Math.sin(rad)} y={100 - 68 * Math.cos(rad)}
                  textAnchor="middle" dominantBaseline="central" fill={color}
                  fontSize="14" fontWeight="bold" fontFamily="monospace">
                  {label}
                </text>
              );
            })}

            {/* Degrés intermédiaires */}
            {[30, 60, 120, 150, 210, 240, 300, 330].map(angle => {
              const rad = (angle * Math.PI) / 180;
              return (
                <text key={angle} x={100 + 68 * Math.sin(rad)} y={100 - 68 * Math.cos(rad)}
                  textAnchor="middle" dominantBaseline="central" fill="#475569"
                  fontSize="9" fontFamily="monospace">
                  {angle}°
                </text>
              );
            })}

            {/* Aiguille Nord - rouge */}
            <polygon points="100,20 96,100 104,100" fill="#ef4444" opacity="0.9" />
            {/* Aiguille Sud - blanche */}
            <polygon points="100,180 96,100 104,100" fill="#94a3b8" opacity="0.5" />

            {/* Centre */}
            <circle cx="100" cy="100" r="6" fill="#1e293b" stroke="#94a3b8" strokeWidth="1" />
            <circle cx="100" cy="100" r="2" fill="#ef4444" />
          </svg>

          {/* Triangle fixe en haut (indicateur de cap) */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1">
            <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[12px] border-l-transparent border-r-transparent border-t-[#8DC63F]"></div>
          </div>
        </div>

        {/* Affichage numérique */}
        <div className="text-center mt-4">
          {heading !== null ? (
            <>
              <p className="text-4xl font-mono font-bold text-white">{deg}°</p>
              <p className="text-lg text-[#8DC63F] font-semibold">{getCardinal(deg)}</p>
            </>
          ) : error ? (
            <div className="space-y-2">
              <p className="text-sm text-red-400">{error}</p>
              <p className="text-xs text-slate-500">Le compas nécessite un appareil mobile avec capteur magnétique</p>
              {permission === 'denied' && (
                <button onClick={requestPermission} className="mt-2 px-4 py-2 bg-[#8DC63F] text-white rounded-lg text-sm font-medium">
                  Réessayer
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Calibration en cours...</p>
              <p className="text-xs text-slate-500">Bougez votre appareil en forme de 8</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompassWidget;
